/* Industry packs: what each one adds, and the switch.
 *
 * Installing writes to the organisation's ERP (roles, fields, the steps work
 * takes), so it shows what it will do first and only the account owner may.
 * Off hides a pack and deletes nothing: its work, its fields and its roles
 * stay, and switching it back on picks up where it left off.
 */
import { useCallback, useEffect, useState } from 'react'
import { Failed, Loading, ScreenState, useToast } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatDatetime } from '../format'
import type { DepartmentPlacing, InstallReport, PackListing, PackPeople, TeamsConnection } from './types'
import { forgetPickers } from './Pickers'
import { forgetPacks } from './usePacks'

export function PacksSettings({ onOpenWork }: { onOpenWork?: () => void }) {
  const erp = useErp()
  const toast = useToast()
  const [packs, setPacks] = useState<PackListing[] | null>(null)
  const [mayManage, setMayManage] = useState(false)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [preview, setPreview] = useState<{ id: string; report: InstallReport } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    setFailed(null)
    erp.packs().then((r) => { setPacks(r.packs); setMayManage(r.may_manage) })
       .catch((e) => setFailed(e as ErpError))
  }, [erp])
  useEffect(load, [load])

  async function check(id: string) {
    setBusy(id)
    try { setPreview({ id, report: await erp.installPack(id, true) }) }
    catch (e) { toast('error', (e as ErpError).message) }
    finally { setBusy(null) }
  }

  async function install(id: string) {
    setBusy(id)
    try {
      const done = await erp.installPack(id, false)
      toast('ok', `Installed ${done.version}: ${done.created.length} added, ${done.updated.length} brought up to date.`)
      setPreview(null); forgetPacks(erp); load()
    } catch (e) { toast('error', (e as ErpError).message) }
    finally { setBusy(null) }
  }

  async function flip(p: PackListing) {
    setBusy(p.id)
    try {
      await erp.switchPack(p.id, !p.enabled)
      toast('ok', p.enabled
        ? `${p.title} is off. Its work is all still here; nothing new can move until it is back on.`
        : `${p.title} is back on.`)
      forgetPacks(erp); load()
    } catch (e) { toast('error', (e as ErpError).message) }
    finally { setBusy(null) }
  }

  if (failed) return <div className="page"><Failed message={failed.message} requestId={failed.requestId} onRetry={load} /></div>
  if (!packs) return <div className="page"><Loading rows={4} label="Reading the packs" /></div>
  if (!packs.length) {
    return <div className="page"><ScreenState title="No packs are offered yet" detail="When a pack for your industry is ready it appears here." /></div>
  }

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 940 }}>
      <div>
        <h1>Industry packs</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '64ch' }}>
          A pack adds the kinds of work your business runs on, in your own words, with the
          steps each one takes and who takes them. Switching one off hides it and keeps
          everything done in it.
          {!mayManage ? ' Only the account owner can install or switch a pack.' : ''}
        </p>
      </div>
      {mayManage && packs.some((p) => p.installed_version) ? <WhoDoesWhat /> : null}
      {mayManage && packs.some((p) => p.installed_version) ? <Departments /> : null}
      {mayManage ? <TeamsPanel /> : null}
      {packs.map((p) => (
        <section key={p.id} className="card card-pad" style={{ display: 'grid', gap: 10 }} aria-label={p.title}>
          <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={{ margin: 0 }}>{p.title}</h2>
              <div className="small muted">
                {p.installed_version ? `Version ${p.installed_version}` : `Version ${p.version} available`}
                {p.installed_at ? ` · installed ${formatDatetime(p.installed_at)}` : ''}
              </div>
            </div>
            {p.installed_version
              ? <span className={`chip ${p.enabled ? 'chip-ok' : 'chip-warn'}`}>{p.enabled ? 'On' : 'Off'}</span>
              : <span className="chip">Not installed</span>}
          </div>
          <p style={{ margin: 0 }}>{p.summary}</p>
          <div className="small"><span className="muted">Adds:</span> {p.kinds.join(', ')}</div>
          <div className="small"><span className="muted">Roles:</span> {p.roles.join(', ')}</div>
          {mayManage ? (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {!p.installed_version || p.upgrade ? (
                <button className="btn btn-primary" disabled={busy === p.id} onClick={() => check(p.id)}>
                  {p.upgrade ? `Upgrade to ${p.version}…` : 'Install…'}
                </button>
              ) : null}
              {p.installed_version ? (
                <button className="btn" disabled={busy === p.id} onClick={() => flip(p)}>
                  {p.enabled ? 'Switch off' : 'Switch on'}
                </button>
              ) : null}
              {p.enabled && onOpenWork ? <button className="btn btn-quiet" onClick={onOpenWork}>Open the work</button> : null}
            </div>
          ) : null}
          {mayManage && p.installed_version ? (
            <label className="check small">
              <input type="checkbox" checked={p.calendar !== false} disabled={busy === p.id}
                     onChange={async (e) => {
                       const on = e.target.checked
                       setBusy(p.id)
                       try {
                         await erp.packSettings(p.id, { calendar: on })
                         toast('ok', on ? 'Its deadlines show in everyone’s Calendar.' : 'Its deadlines are kept out of the Calendar.')
                         forgetPacks(erp); load()
                       } catch (err) { toast('error', (err as ErpError).message) } finally { setBusy(null) }
                     }} />
              Show its deadlines in everyone’s Calendar in the desk (each person can still hide them)
            </label>
          ) : null}
          {mayManage && p.installed_version ? (
            <label className="small" style={{ display: 'grid', gap: 4, maxWidth: 520 }}>
              How time on its work is recorded
              <select className="select" value={p.time ?? 'both'} disabled={busy === p.id}
                      onChange={async (e) => {
                        const time = e.target.value as 'both' | 'automatic' | 'manual'
                        setBusy(p.id)
                        try {
                          await erp.packSettings(p.id, { time })
                          toast('ok', 'Saved. It applies from the next step anybody takes.')
                          forgetPacks(erp); load()
                        } catch (err) { toast('error', (err as ErpError).message) } finally { setBusy(null) }
                      }}>
                <option value="both">A timer, and hours people can correct</option>
                <option value="automatic">The timer only (start, and sending stops it)</option>
                <option value="manual">Typed hours only</option>
              </select>
            </label>
          ) : null}
          {preview?.id === p.id ? (
            <div className="card card-pad" role="status" style={{ background: 'var(--sunk)', display: 'grid', gap: 8 }}>
              <b>What installing does</b>
              <div className="small">
                Adds {preview.report.created.length} things to your ERP
                {preview.report.updated.length ? ` and updates ${preview.report.updated.length}` : ''}
                {preview.report.unchanged ? `; ${preview.report.unchanged} are already in place` : ''}.
                Nothing is deleted.
              </div>
              <details className="small">
                <summary>See the list</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {[...preview.report.created, ...preview.report.updated].map((x) => <li key={x}>{x}</li>)}
                </ul>
              </details>
              <div className="row" style={{ gap: 8 }}>
                <button className="btn btn-primary" disabled={busy === p.id} onClick={() => install(p.id)}>
                  {busy === p.id ? 'Installing…' : 'Install now'}
                </button>
                <button className="btn btn-quiet" onClick={() => setPreview(null)}>Cancel</button>
              </div>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  )
}

/** Connecting the organisation's Jackdesk Teams app, so work posts cards. */
function TeamsPanel() {
  const erp = useErp()
  const toast = useToast()
  const [teams, setTeams] = useState<TeamsConnection | null>(null)
  const [baseUrl, setBaseUrl] = useState('https://teams.jackdesk.com/cp')
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => { erp.teams().then(setTeams).catch(() => setTeams({ connected: false, channels: [] })) }, [erp])
  if (!teams) return null
  return (
    <section className="card card-pad" style={{ display: 'grid', gap: 10 }} aria-label="Teams">
      <div className="row row-between">
        <h2 style={{ margin: 0 }}>Cards in Teams</h2>
        <span className={`chip ${teams.connected ? 'chip-ok' : ''}`}>{teams.connected ? 'Connected' : 'Not connected'}</span>
      </div>
      {teams.builtin ? (
        <div className="small" style={{ maxWidth: '70ch' }}>
          Through Team chat's own <b>Work</b> app: each project chooses its channel on the job screen; every job
          gets a card there with how far the project has got, each step and each comment goes in the job's
          thread, and a reply in that thread comes back here as the person's comment. Nothing to connect.
          {teams.problem ? <div style={{ color: 'var(--danger)' }}>{teams.problem}</div> : null}
        </div>
      ) : teams.connected ? (
        <>
          <div className="small">
            Posting as app <b>{teams.app_id}</b> at {teams.base_url}, in {teams.channels.length} channel
            {teams.channels.length === 1 ? '' : 's'}. Each job chooses its channel on the job screen.
          </div>
          {teams.problem ? <div className="small" style={{ color: 'var(--danger)' }}>{teams.problem}</div> : null}
          <div>
            <button className="btn" disabled={busy} onClick={async () => {
              setBusy(true)
              try { setTeams(await erp.disconnectTeams()); toast('ok', 'Disconnected. No more cards are posted.') }
              catch (e) { toast('error', (e as ErpError).message) }
              finally { setBusy(false) }
            }}>Disconnect</button>
          </div>
        </>
      ) : (
        <form style={{ display: 'grid', gap: 10 }} onSubmit={async (e) => {
          e.preventDefault(); setBusy(true); setProblem(null)
          try { setTeams(await erp.connectTeams(baseUrl.trim(), token.trim())); setToken(''); toast('ok', 'Teams connected.') }
          catch (err) { setProblem((err as ErpError).message) }
          finally { setBusy(false) }
        }}>
          <p className="small muted" style={{ margin: 0, maxWidth: '64ch' }}>
            In Teams, install an app with the <b>chat:write</b> and <b>channels:read</b> permissions, add it to
            the channels that should get cards, and paste its bot token here. The token is kept encrypted and
            never shown again.
          </p>
          <div className="field">
            <label htmlFor="teams_base">Teams address</label>
            <input id="teams_base" className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="teams_token">Bot token</label>
            <input id="teams_token" className="input" type="password" autoComplete="off" value={token}
                   placeholder="jdb-…" onChange={(e) => setToken(e.target.value)} />
          </div>
          {problem ? <div role="alert" className="small" style={{ color: 'var(--danger)' }}>{problem}</div> : null}
          <div><button className="btn btn-primary" disabled={busy || !token.trim()}>{busy ? 'Checking…' : 'Connect'}</button></div>
        </form>
      )}
    </section>
  )
}

/** Who holds which of the packs' roles, for everyone at the organisation.
 *  Ticking a role for somebody who has never opened the ERP makes their ERP
 *  user there and then, so work can be given to them on day one. */
function WhoDoesWhat() {
  const erp = useErp()
  const toast = useToast()
  const [data, setData] = useState<PackPeople | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const load = useCallback(() => {
    erp.packPeople().then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp])
  useEffect(load, [load])
  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
  if (!data) return <Loading rows={3} label="Reading who does what" />
  const shown = data.people.filter((p) => !filter || `${p.name} ${p.email}`.toLowerCase().includes(filter.toLowerCase()))

  const setRoles = (email: string, roles: string[], inErp?: boolean) =>
    setData((d) => d && { ...d, people: d.people.map((p) => p.email === email
      ? { ...p, roles, in_erp: inErp ?? p.in_erp } : p) })

  async function toggle(email: string, held: string[], role: string) {
    const next = held.includes(role) ? held.filter((r) => r !== role) : [...held, role]
    // The box answers at once; a refusal puts it back and says why.
    setRoles(email, next)
    setBusy(email)
    try {
      const done = await erp.setPackRoles(email, next)
      if (done.created) toast('ok', `${email} can now be given work; they have not signed in yet.`)
      setRoles(email, done.roles, true)
    } catch (e) {
      setRoles(email, held)
      toast('error', (e as ErpError).message)
    } finally { setBusy(null) }
  }

  return (
    <section className="card" aria-label="Who does what" style={{ overflowX: 'auto' }}>
      <div className="card-pad row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>Who does what</h2>
          <div className="small muted">Everyone at your organisation, and the pack roles each holds.</div>
        </div>
        <input className="input" style={{ maxWidth: 240 }} placeholder="Find a person" aria-label="Find a person"
               value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>
      {data.problem ? <div className="card-pad small" role="status" style={{ paddingTop: 0 }}>{data.problem}</div> : null}
      <table className="tbl">
        <thead>
          <tr>
            <th>Person</th>
            {data.roles.map((r) => <th key={r.name} className="small" style={{ whiteSpace: 'nowrap' }}>{r.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {shown.map((p) => (
            <tr key={p.email} style={{ cursor: 'default' }}>
              <td>
                <div>{p.name}</div>
                <div className="small muted">{p.email}{p.in_erp ? '' : ' · not signed in yet'}</div>
              </td>
              {data.roles.map((r) => (
                <td key={r.name} style={{ textAlign: 'center' }}>
                  <input type="checkbox" aria-label={`${p.name}: ${r.name}`} disabled={busy === p.email}
                         checked={p.roles.includes(r.name)} onChange={() => toggle(p.email, p.roles, r.name)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

/** Your departments, placed under the ERP's (owner, 2026-10-08: "we can have subdepartments
 *  (jackdesk) under ERP departments since I dont want the Accounts or cost center, etc gets
 *  affected"). The ERP's departments — which accounts and cost centres hang on — never move;
 *  each of yours sits under one of them, and its people are that department's people when
 *  work is given to a department. */
function Departments() {
  const erp = useErp()
  const toast = useToast()
  const [data, setData] = useState<DepartmentPlacing | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [draft, setDraft] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState(false)
  const load = useCallback(() => {
    setFailed(null)
    erp.departmentPlacing().then((d) => { setData(d); setDraft({}) }).catch((e) => setFailed(e as ErpError))
  }, [erp])
  useEffect(load, [load])
  if (failed) return <Failed message={failed.message} requestId={failed.requestId} onRetry={load} />
  if (!data) return <Loading rows={3} label="Reading your departments" />

  const chosen = (id: number, current: string | null) => (id in draft ? draft[id] : current ?? '')
  const changed = data.jackdesk.filter((d) => d.id in draft && (draft[d.id] || null) !== d.parent)
  const unplaced = data.jackdesk.filter((d) => !d.parent)

  async function save() {
    setBusy(true)
    try {
      const done = await erp.placeDepartments(changed.map((d) => ({ jackdesk: d.id, parent: draft[d.id] || null })))
      const bad = (done.results ?? []).filter((r) => !['placed', 'same', 'taken_out', 'unchanged'].includes(r.result))
      setData(done); setDraft({}); forgetPickers()
      // Each says why; the others were placed all the same.
      toast(bad.length ? 'error' : 'ok', bad.length
        ? bad.map((r) => r.message ?? `${r.result.replace(/_/g, ' ')}.`).join(' ')
        : 'Departments placed.')
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  function takeSuggestions() {
    const next: Record<number, string> = { ...draft }
    for (const d of unplaced) if (d.suggestion && !(d.id in next)) next[d.id] = d.suggestion
    setDraft(next)
  }

  return (
    <section className="card" aria-label="Departments" style={{ overflowX: 'auto' }}>
      <div className="card-pad" style={{ display: 'grid', gap: 6 }}>
        <div className="row row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Departments</h2>
          {unplaced.length ? <span className="chip chip-warn">{unplaced.length} not placed</span> : <span className="chip chip-ok">All placed</span>}
        </div>
        <p className="small muted" style={{ margin: 0, maxWidth: '72ch' }}>
          Your people are grouped in your own departments (the Store's People page). The ERP keeps its own,
          which accounts and cost centres use — those never change here. Put each of yours under one of the
          ERP's: work given to an ERP department then goes to the people in yours, and nothing in your
          accounts moves. A department with the same name as an ERP one is simply that department.
        </p>
        {data.problem ? <div className="small" role="status" style={{ color: 'var(--warn)' }}>{data.problem}</div> : null}
      </div>
      {data.jackdesk.length ? (
        <table className="tbl">
          <thead><tr><th>Your department</th><th>People</th><th>Under ERP department</th></tr></thead>
          <tbody>
            {data.jackdesk.map((d) => (
              <tr key={d.id} style={{ cursor: 'default' }}>
                <td>{d.name}{d.same ? <div className="small muted">the same as the ERP's</div> : null}</td>
                <td className="small">{d.people}</td>
                <td>
                  <select className="select" aria-label={`${d.name}: under which ERP department`}
                          value={chosen(d.id, d.parent)} disabled={busy}
                          onChange={(e) => setDraft((x) => ({ ...x, [d.id]: e.target.value }))}>
                    <option value="">Not placed</option>
                    {data.erp.map((e) => (
                      <option key={e.name} value={e.name}>{e.label}{e.pack ? '' : ' (ERP)'}</option>
                    ))}
                  </select>
                  {!d.parent && d.suggestion && !(d.id in draft) ? (
                    <div className="small muted">Suggested: {data.erp.find((e) => e.name === d.suggestion)?.label}</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="card-pad small muted" style={{ paddingTop: 0 }}>
          No departments yet. Make them in the Store (People → Departments), then place them here.
        </div>
      )}
      {data.jackdesk.length ? (
        <div className="card-pad row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={busy || !changed.length} onClick={() => void save()}>
            {busy ? 'Saving…' : changed.length ? `Save ${changed.length}` : 'Save'}
          </button>
          {unplaced.some((d) => d.suggestion && !(d.id in draft)) ? (
            <button className="btn btn-quiet" disabled={busy} onClick={takeSuggestions}>Use the suggestions</button>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
